import { Box } from "@chakra-ui/react";
import type { GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { defaultColDef, getColumnDefinitions } from "./columnDefinitions";

interface ABTestsTableProps {
  abTests: any[];
  onSelectionChanged: (selectedRows: any[]) => void;
  onToggleStatus: (id: string, currentStatus: string) => void;
  loadingTests: Set<string>;
}

export const ABTestsTable = forwardRef<AgGridReact, ABTestsTableProps>(
  ({ abTests, onSelectionChanged, onToggleStatus, loadingTests }, ref) => {
    const navigate = useNavigate();
    const columnDefs = useMemo(
      () => getColumnDefinitions(onToggleStatus, loadingTests),
      [onToggleStatus, loadingTests]
    );

    const onGridReady = useCallback((event: GridReadyEvent) => {
      console.log("AG Grid is ready!", event.api);
      // Auto-size all columns based on content
      event.api.autoSizeAllColumns();
    }, []);

    const handleSelectionChanged = useCallback(
      (event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes();
        const selectedData = selectedNodes.map((node) => node.data);
        onSelectionChanged(selectedData);
      },
      [onSelectionChanged]
    );

    return (
      <Box className="ag-theme-alpine" height="600px">
          <AgGridReact
            ref={ref}
            rowData={abTests}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            onSelectionChanged={handleSelectionChanged}
            onRowDoubleClicked={(params) => {
              if (params.data?.id) {
                navigate(`/website/ab-test/${params.data.id}/edit`);
              }
            }}
            getRowStyle={() => ({
              cursor: "pointer"
            })}
            onFirstDataRendered={(params) => {
              // Auto-size columns when data is first rendered
              params.api.autoSizeAllColumns();
            }}
            onGridSizeChanged={(params) => {
              // Auto-size columns when grid is resized
              params.api.autoSizeAllColumns();
            }}
            animateRows={true}
            enableCharts={true}
            sideBar={{
              toolPanels: [
                {
                  id: "columns",
                  labelDefault: "Columns",
                  labelKey: "columns",
                  iconKey: "columns",
                  toolPanel: "agColumnsToolPanel"
                },
                {
                  id: "filters",
                  labelDefault: "Filters",
                  labelKey: "filters",
                  iconKey: "filter",
                  toolPanel: "agFiltersToolPanel"
                }
              ],
              hiddenByDefault: true
            }}
            rowSelection={{
              mode: "multiRow",
              checkboxes: true,
              enableClickSelection: false,
              headerCheckbox: true
            }}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20, 50]}
            paginationAutoPageSize={false}
            suppressPaginationPanel={false}
            paginationNumberFormatter={(params) => {
              return params.value.toLocaleString();
            }}
            localeText={{
              page: "Page",
              more: "More",
              to: "to",
              of: "of",
              next: "Next",
              last: "Last",
              first: "First",
              previous: "Previous",
              loadingOoo: "Loading...",
              noRowsToShow: "No A/B tests to show"
            }}
            rowHeight={35}
            loading={false}
          />
      </Box>
    );
  }
);
